import { isAxiosError } from "axios";

import type { Auth } from "@/types";

import { refreshAuthToken } from "./helpers";

/**
 * Tablar orasidagi qulf nomi. Bitta brauzerdagi hamma tab bitta cookie
 * bilan ishlaydi, ya'ni ikki tab bir vaqtda yangilasa ikkalasi ham bir xil
 * refresh token'ni yuborardi.
 */
export const REFRESH_LOCK_NAME = "lu-auth-refresh";

/** Bu tabda hozir ketayotgan yangilash — bitta va'da, hamma kutadi. */
let inflight: Promise<Auth> | null = null;

/**
 * Web Locks bo'lsa ish tablar orasida navbat bilan bajariladi: ikkinchi tab
 * birinchisi yangilab bo'lgach, YANGI cookie bilan so'raydi. API bo'lmasa
 * (eski brauzer, jsdom) ish to'g'ridan-to'g'ri bajariladi — backendning
 * grace oynasi (ADR 0009) bunday poygani ham ko'taradi.
 */
const withCrossTabLock = <T>(work: () => Promise<T>): Promise<T> => {
  const locks = typeof navigator === "undefined" ? undefined : navigator.locks;
  if (!locks?.request) return work();
  return locks.request(REFRESH_LOCK_NAME, { mode: "exclusive" }, work) as Promise<T>;
};

/**
 * Yangi access token oladi — bu tabda BIR VAQTDA faqat bitta so'rov.
 *
 * Ilgari ikki joy mustaqil yangilardi: do'kon (reload'dan keyin) va 401 ni
 * ushlagan interceptor. Reload paytida `/auth/me` token tiklanmasdan ketib
 * 401 olardi, interceptor esa ikkinchi yangilashni o'sha cookie bilan
 * yuborardi — backend buni o'g'irlik deb bilib, sessiyani yopardi va
 * foydalanuvchi har refresh'da login'ga uloqtirilardi.
 *
 * Endi ikkala yo'l ham shu funksiyani chaqiradi va bir xil va'dani oladi.
 */
export const refreshSessionOnce = (): Promise<Auth> => {
  if (inflight) return inflight;

  const run = withCrossTabLock(async () => {
    const { data: body } = await refreshAuthToken();
    return body.data;
  });

  inflight = run.finally(() => {
    inflight = null;
  });
  return inflight;
};

/** Hozir yangilash ketyaptimi — so'rovlar token'siz ketmasin deb kutish uchun. */
export const getInflightRefresh = (): Promise<Auth> | null => inflight;

/**
 * Yangilash SESSIYA YO'QLIGI sababli yiqildimi (qaytarib bo'lmaydi)?
 *
 * Faqat server "yo'q" degan javobi (401/403) sessiyani yopadi. Tarmoq
 * uzilishi, 502 (backend qayta ishga tushyapti) yoki 5xx — vaqtinchalik:
 * ular uchun foydalanuvchini chiqarib yuborish ishini yo'qotish degani.
 */
export const isSessionRejected = (error: unknown): boolean => {
  if (!isAxiosError(error)) return false;
  const status = error.response?.status;
  return status === 401 || status === 403;
};

/**
 * Vaqtinchalik xatoda yangilashni qayta urinish kechikishlari; oxirgisi
 * takrorlanaveradi.
 */
export const REFRESH_RETRY_DELAYS_MS = [500, 1500, 3000, 5000, 10_000, 30_000] as const;

/** `Retry-After` (soniya) millisekundda; sarlavha bo'lmasa — `null`. */
export const retryAfterMs = (error: unknown): number | null => {
  if (!isAxiosError(error)) return null;
  const raw: unknown = error.response?.headers?.["retry-after"];
  const seconds = Number(raw);
  return raw !== undefined && Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : null;
};

/**
 * Yangilash yiqilgach keyingi urinishgacha kutish; `null` — to'xtash
 * (server sessiyani rad etdi, foydalanuvchi chiqariladi).
 *
 * Ilgari uch urinishdan keyin HAR QANDAY xato sessiyani yopardi: 429
 * (chegara) yoki qayta ishga tushayotgan backend moderatorni ish
 * o'rtasida login sahifasiga uloqtirardi. Endi faqat server "sessiya
 * yo'q" desa chiqiladi; qolganida kutib, qayta uriniladi.
 */
export const refreshRetryDelay = (attempt: number, error: unknown): number | null => {
  if (isSessionRejected(error)) return null;
  const index = Math.min(Math.max(attempt, 0), REFRESH_RETRY_DELAYS_MS.length - 1);
  return Math.max(REFRESH_RETRY_DELAYS_MS[index], retryAfterMs(error) ?? 0);
};
